import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { contractsAPI, accountsAPI } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Building2, Calendar, DollarSign, TrendingUp, MapPin, FileText, RefreshCw, Sparkles } from 'lucide-react';
import { formatCurrency, formatDate, getDaysUntil, getCompetitivenessColor, getCompetitivenessLabel, getStatusColor } from '@/lib/utils';
import RateAnalysisPanel from '@/components/RateAnalysisPanel';
import PricingRecommendationPanel from '@/components/PricingRecommendationPanel';

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: contract, isLoading, refetch } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => contractsAPI.getById(id).then((res) => res.data),
  });

  const { data: account } = useQuery({
    queryKey: ['account', contract?.account_id],
    queryFn: () => accountsAPI.getById(contract.account_id).then((res) => res.data),
    enabled: !!contract?.account_id,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-[#09409C]" />
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="p-8 text-center">
        <p className="text-xl text-muted-foreground">Contract not found</p>
        <Button onClick={() => navigate('/contracts')} className="mt-4">
          Back to Contracts
        </Button>
      </div>
    );
  }

  const daysToRenewal = getDaysUntil(contract.end_date);
  const statusColor = getStatusColor(contract.status);
  const competitivenessColor = getCompetitivenessColor(contract.competitiveness_score);

  return (
    <div className="p-8 space-y-6" data-testid="contract-detail">
      <div>
        <Button variant="ghost" onClick={() => navigate('/contracts')} className="mb-4" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Contracts
        </Button>
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Contract <span className="text-gradient-primary">{contract.contract_number}</span>
              </h1>
              <Badge className={statusColor === 'green' ? 'bg-green-100 text-green-800' : statusColor === 'yellow' ? 'bg-yellow-100 text-yellow-800' : statusColor === 'red' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}>
                {contract.status.replace('_', ' ')}
              </Badge>
            </div>
            <p className="text-lg text-muted-foreground flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {account?.name || 'Loading...'}
            </p>
          </div>
          <Button onClick={() => refetch()} variant="outline" data-testid="refresh-contract-button">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="key-metrics">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardDescription>Annual Value</CardDescription>
              <DollarSign className="w-4 h-4 text-[#229644]" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {formatCurrency(contract.annual_value)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardDescription>Days to Expiration</CardDescription>
              <Calendar className="w-4 h-4 text-[#FF521C]" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {daysToRenewal > 0 ? daysToRenewal : 'Expired'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Ends {formatDate(contract.end_date)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardDescription>Competitiveness</CardDescription>
              <TrendingUp className="w-4 h-4 text-[#146CBB]" />
            </div>
          </CardHeader>
          <CardContent>
            {contract.competitiveness_score ? (
              <>
                <div className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {contract.competitiveness_score}
                </div>
                <Badge className={`mt-1 text-xs ${competitivenessColor === 'green' ? 'bg-green-100 text-green-800' : competitivenessColor === 'blue' ? 'bg-blue-100 text-blue-800' : competitivenessColor === 'yellow' ? 'bg-yellow-100 text-yellow-800' : competitivenessColor === 'orange' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'}`}>
                  {getCompetitivenessLabel(contract.competitiveness_score)}
                </Badge>
              </>
            ) : (
              <div className="text-2xl font-bold text-gray-400">N/A</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardDescription>Base Rate</CardDescription>
              <DollarSign className="w-4 h-4 text-[#09409C]" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              ${contract.base_rate}
            </div>
            <p className="text-xs text-muted-foreground mt-1">{contract.rate_type}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-4" data-testid="contract-tabs">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="ai-analysis" data-testid="tab-ai-analysis">
            <Sparkles className="w-4 h-4 mr-2" />
            AI Analysis
          </TabsTrigger>
          <TabsTrigger value="locations" data-testid="tab-locations">Service Locations</TabsTrigger>
          <TabsTrigger value="rates" data-testid="tab-rates">Rate Schedules</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Contract Details</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Start Date</p>
                <p className="text-lg font-semibold mt-1">{formatDate(contract.start_date)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">End Date</p>
                <p className="text-lg font-semibold mt-1">{formatDate(contract.end_date)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Term</p>
                <p className="text-lg font-semibold mt-1">{contract.term_months} months</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Auto Renewal</p>
                <p className="text-lg font-semibold mt-1">{contract.auto_renewal ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Notice Period</p>
                <p className="text-lg font-semibold mt-1">{contract.notice_period_days} days</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Salesforce ID</p>
                <p className="text-lg font-semibold mt-1">{contract.salesforce_id || 'N/A'}</p>
              </div>
            </CardContent>
          </Card>

          {account && (
            <Card>
              <CardHeader>
                <CardTitle>Account Information</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Account Name</p>
                  <p className="text-lg font-semibold mt-1">{account.name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Industry</p>
                  <p className="text-lg font-semibold mt-1">{account.industry || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Customer Status</p>
                  <Badge className="mt-1">{account.customer_status}</Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Relationship Start</p>
                  <p className="text-lg font-semibold mt-1">
                    {account.relationship_start_date ? formatDate(account.relationship_start_date) : 'N/A'}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="ai-analysis" className="space-y-4">
          <div className="grid grid-cols-1 gap-6">
            <RateAnalysisPanel 
              contractId={contract.id} 
              contract={contract}
              onScoreUpdate={(newScore) => {
                refetch();
              }}
            />
            <PricingRecommendationPanel 
              contractId={contract.id}
              currentRate={contract.base_rate}
            />
          </div>
        </TabsContent>

        <TabsContent value="locations" className="space-y-4">
          {contract.service_locations && contract.service_locations.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {contract.service_locations.map((location, index) => (
                <Card key={location.id || index}>
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <MapPin className="w-4 h-4 mr-2 text-[#FF521C]" />
                      Service Location {index + 1}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Address</p>
                      <p className="text-sm mt-1">
                        {location.address}, {location.city}, {location.state} {location.zip_code}
                      </p>
                    </div>
                    {location.utility_territory && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Utility Territory</p>
                        <p className="text-sm mt-1">{location.utility_territory}</p>
                      </div>
                    )}
                    {location.iso_region && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">ISO Region</p>
                        <Badge variant="outline">{location.iso_region}</Badge>
                      </div>
                    )}
                    {location.annual_usage_kwh && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Annual Usage</p>
                        <p className="text-sm mt-1">{location.annual_usage_kwh.toLocaleString()} kWh</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <MapPin className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No service locations found</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="rates" className="space-y-4">
          {contract.rate_schedules && contract.rate_schedules.length > 0 ? (
            <div className="space-y-4">
              {contract.rate_schedules.map((schedule, index) => (
                <Card key={schedule.id || index}>
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <FileText className="w-4 h-4 mr-2 text-[#09409C]" />
                      Rate Schedule {index + 1}
                    </CardTitle>
                    <CardDescription>
                      Effective: {formatDate(schedule.effective_date)}
                      {schedule.expiration_date && ` - ${formatDate(schedule.expiration_date)}`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Energy Rate</p>
                        <p className="text-lg font-semibold mt-1">${schedule.energy_rate}/kWh</p>
                      </div>
                      {schedule.capacity_rate && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Capacity Rate</p>
                          <p className="text-lg font-semibold mt-1">${schedule.capacity_rate}/kW</p>
                        </div>
                      )}
                      {schedule.transmission_rate && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Transmission</p>
                          <p className="text-lg font-semibold mt-1">${schedule.transmission_rate}</p>
                        </div>
                      )}
                      {schedule.ancillary_rate && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Ancillary</p>
                          <p className="text-lg font-semibold mt-1">${schedule.ancillary_rate}</p>
                        </div>
                      )}
                      {schedule.renewable_adder && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Renewable Adder</p>
                          <p className="text-lg font-semibold mt-1">${schedule.renewable_adder}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No rate schedules found</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
